import { inject, Injectable, OnDestroy } from "@angular/core";
import { WebsocketService } from "../../../../../../../shared/src/lib/services/websocket.service";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { BehaviorSubject, combineLatest, Observable, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "../../../../../../../shared/src";
import { Router } from "@angular/router";
import { FeatureFlagService } from "../../../../../../../shared/src/lib/services/feature-flag.service";
import { HttpClient } from "@angular/common/http";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";

@Injectable({
  providedIn: "root",
})
export class MarketOpsService implements OnDestroy {
  http = inject(HttpClient);
  subscriptions: Subscription = new Subscription();
  market: BehaviorSubject<any> = new BehaviorSubject(null);
  racingNavItems: BehaviorSubject<any> = new BehaviorSubject([]);
  apiService = inject(ApiService);
  wsService = inject(WebsocketService);
  liveUpdateService = inject(LiveUpdateService);
  topics$: BehaviorSubject<any> = new BehaviorSubject([]);
  router = inject(Router);
  marketPool$: BehaviorSubject<any> = new BehaviorSubject([]);
  allMarkets$: BehaviorSubject<any> = new BehaviorSubject([]);
  navMarket$: BehaviorSubject<string> = new BehaviorSubject("");
  navBasedSortPriority$: BehaviorSubject<Map<string, any>> = new BehaviorSubject<Map<string, any>>(new Map());
  featureFlagService = inject(FeatureFlagService);
  loader = inject(LoaderService);

  constructor() {
    this.topics$.subscribe((topics) => {
      this.resendOnReconnection(topics);
    });
    this.resendOnReconnection(this.topics$.value);
  }

  get singleMarketSelected() {
    return this.router.url.includes("/market/");
  }

  setMarketPool(markets: any[], config?: { initWith: string; initCompareKey: string }) {
    const currentMarkets = this.marketPool$.getValue();
    const seen = new Set();
    const nFilteredMarkets = [...currentMarkets, ...markets]
      .filter((market) => market?.eventId === this.eventId)
      .filter((market) => {
        const key = market.topic || market.marketId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    const nSortedMarkets = this.sortMarkets(nFilteredMarkets);
    this.marketPool$.next(nSortedMarkets);
    let allMarkets = nSortedMarkets;

    if (config) {
      const { initWith, initCompareKey } = config ?? {};
      allMarkets = nSortedMarkets?.filter((market) => market[initCompareKey] === initWith);
    }

    this.allMarkets$.next(allMarkets?.filter((market) => !market?.isAsianHandicap && !market?.showUnderFancy));
  }

  sortMarkets(markets: any[]) {
    const marketsToSort = [...markets];

    return marketsToSort.sort((a, b) => {
      const priorities = ["isMain", "isMOddsBookmaker", "isBookmaker", "isFancy", "isLine"];

      for (const key of priorities) {
        const aVal = a[key] ? 1 : 0;
        const bVal = b[key] ? 1 : 0;
        if (aVal !== bVal) {
          return bVal - aVal;
        }
      }

      const bothAreLine = a?.isLine && b?.isLine;
      const isNotCricket = a?.eventType?.id !== "4";

      if (bothAreLine || isNotCricket) {
        const aPriority = a?.priority;
        const bPriority = b?.priority;

        if (typeof aPriority === "number" && typeof bPriority === "number") {
          return (aPriority ?? Infinity) - (bPriority ?? Infinity);
        } else if (typeof aPriority === "string" && typeof bPriority === "string") {
          return aPriority?.localeCompare(bPriority);
        }
      }

      return 0;
    });
  }

  removeMarket(id: any, field: string) {
    this.allMarkets$.next(this.allMarkets$.getValue().filter((market: any) => market[field] !== id));
    this.marketPool$.next(this.marketPool$.getValue().filter((market: any) => market[field] !== id));
  }

  get eventId() {
    return this.router.url.match(/content:event-markets\/([^/.]+)/)?.[1];
  }

  get event() {
    return this.market.value?.event;
  }

  get eventType() {
    return this.market.value?.eventType;
  }

  setTopics(newTopics: string[], type?: string) {
    // Get the current topics list
    const currentTopics = this.topics$.value;

    // Convert currentTopics to a Set for faster lookup (O(1) lookup time)
    const currentTopicsSet = new Set(currentTopics);

    // Filter out topics from newTopics that are already in currentTopics
    const filteredTopics = newTopics.filter((item) => !currentTopicsSet.has(item));

    if (type === "merge") {
      // If merging, add the filtered topics (only the new ones) to the current topics
      this.topics$.next([...currentTopics, ...filteredTopics]);
    } else {
      // If not merging, replace current topics with newTopics
      this.topics$.next(newTopics);
    }
  }

  getTopics(): Observable<Array<string>> {
    return this.topics$.pipe(
      map((topics: Array<string>) =>
        topics.map((topic: string) => {
          if (String(topic).startsWith("sb")) {
            const parts = topic.split("_");
            if (parts.length > 2) {
              return parts?.[1];
            }
          }
          return String(topic).substring(3);
        }),
      ),
    );
  }

  resendOnReconnection(topics: string[]) {
    // console.log("TOPICS TO BET SET => ", topics);
    this.liveUpdateService.getConnectionStatus().subscribe({
      next: (status) => {
        if (status && topics?.length > 0) {
          this.wsService.sendMessage({ topics });
        }
      },
    });
  }

  transformBmFancyRunners(runners: any[]) {
    return runners?.reduce((acc, runner) => {
      if (runner?.selectionId) {
        acc[runner.selectionId] = runner;
      }
      return acc;
    }, {});
  }

  transformRunners(runners: any[]) {
    return runners?.reduce((acc, runner) => {
      const { id, ex, st, tm } = runner || {};
      const { abp, abs, alp, als, tvp, tvs, hc } = ex || {};
      acc[hc ? `${id}_${hc}` : id] = {
        abp,
        abs,
        alp,
        als,
        tvp,
        tvs,
        status: st,
        totalMatched: tm,
        hc,
      };
      return acc;
    }, {});
  }

  marketTracker(market: any) {
    return market?.topic ?? market?.marketId;
  }

  getMarket(marketId: string, compareKey?: string) {
    return this.marketPool$.pipe(map((markets) => markets.find((market: any) => market?.[compareKey ?? "marketId"] === marketId)));
  }

  isMarketSelected(marketId: string) {
    return this.router.url.includes(marketId);
  }

  isMarketLoading() {
    return combineLatest([
      this.loader.isLoading("/navigation/event-market"),
      this.loader.isLoading("/navigation/race-market"),
      this.loader.isLoading("/v1/api/navigation_list"),
      this.loader.isLoading("/navigation/event"),
      this.loader.isLoading("/sb/bm-fancy/event"),
      this.loader.isLoading("/market/status"),
    ]).pipe(
      map(
        ([loadingEventMarkets, loadingRaceMarkets, loadingNavigationList, loadingNavEvents, loadingSbMarkets, loadingMarketStatuses]) =>
          loadingEventMarkets || loadingRaceMarkets || loadingNavigationList || loadingNavEvents || loadingSbMarkets || loadingMarketStatuses,
      ),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
